import React, { useState, useEffect } from "react";
import { Button, TextInput, Textarea, Select } from "@mantine/core";
import apiService from "../../services/api";
import type { TrainerSetupProps } from "../../types";
import { useConfirm } from "../shared/useConfirm";
import { formatPhoneNumber } from "../../utils/phone";

interface TrainerFormDataLocal {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  address: string;
  business_name: string;
  time_zone: string;
  business_street: string;
  business_apt: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  business_address: string;
  credentials: string[];
  training_philosophy: string[];
  areas_of_expertise: string[];
  experience_years: string;
  year_began_training: string;
  first_10_min_rate: string;
  per_minute_rate: string;
  bio: string;
  legal_states: string[];
  formal_degree: string;
  professional_school: string;
  other_credentials: string;
  other_sport_judge: string;
  breed_expert: string;
  ccf_affiliation: string;
  [key: string]: unknown;
}

interface UploadedDocument {
  filename: string;
  file_path?: string;
  pending?: boolean;
}

interface BackgroundFormData {
  legal_name: string;
  date_of_birth: string;
  consent: boolean;
}

interface StateOption {
  value: string;
  label: string;
}

interface TabInfo {
  id: string;
  label: string;
  icon: string;
  short: string;
}

const DRAFT_STORAGE_KEY = "trainerSetupDraft";

interface TrainerSetupDraft {
  formData?: Partial<TrainerFormDataLocal>;
  activeTab?: string;
  completedTabs?: string[];
  uploadedDocuments?: Record<string, UploadedDocument | null>;
  backgroundForm?: Partial<BackgroundFormData>;
}

const loadDraft = (): TrainerSetupDraft => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrainerSetupDraft) : {};
  } catch {
    return {};
  }
};

const TrainerSetup: React.FC<TrainerSetupProps> = ({ onBackToHome, onSetupComplete, userData }) => {
  const { confirm, confirmModal } = useConfirm();
  const draft = loadDraft();

  const defaultFormData: TrainerFormDataLocal = {
    first_name: "",
    last_name: "",
    email: userData?.email || localStorage.getItem("email") || "",
    phone_number: "",
    address: "",
    business_name: "",
    time_zone: "",
    business_street: "",
    business_apt: "",
    business_city: "",
    business_state: "",
    business_zip: "",
    business_address: "",
    credentials: [],
    training_philosophy: [],
    areas_of_expertise: [],
    experience_years: "",
    year_began_training: "",
    first_10_min_rate: "",
    per_minute_rate: "",
    bio: "",
    legal_states: [],
    formal_degree: "",
    professional_school: "",
    other_credentials: "",
    other_sport_judge: "",
    breed_expert: "",
    ccf_affiliation: "",
  };

  const [formData, setFormData] = useState<TrainerFormDataLocal>({
    ...defaultFormData,
    ...(draft.formData || {}),
    email: userData?.email || draft.formData?.email || localStorage.getItem("email") || "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(draft.activeTab || "intro");
  const [completedTabs, setCompletedTabs] = useState<Set<string>>(
    new Set(draft.completedTabs || []),
  );
  const [credentialOptions, setCredentialOptions] = useState<string[]>([]);
  const [trainingPhilosophyOptions, setTrainingPhilosophyOptions] = useState<string[]>([]);
  const [areasOfExpertiseOptions, setAreasOfExpertiseOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [uploadedDocuments, setUploadedDocuments] = useState<
    Record<string, UploadedDocument | null>
  >(() => {
    const base = {
      resume: null,
      certificates: null,
      degrees: null,
      insurance: null,
    } as Record<string, UploadedDocument | null>;
    // Drop any pending entries from the draft — the underlying File object can't be
    // recovered after a refresh, so only keep successfully uploaded documents.
    const persisted = draft.uploadedDocuments || {};
    for (const [k, v] of Object.entries(persisted)) {
      if (v && !v.pending) base[k] = v;
    }
    return base;
  });
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [backgroundForm, setBackgroundForm] = useState<BackgroundFormData>({
    legal_name: draft.backgroundForm?.legal_name || "",
    date_of_birth: draft.backgroundForm?.date_of_birth || "",
    consent: draft.backgroundForm?.consent || false,
  });
  const [backgroundSubmitting, setBackgroundSubmitting] = useState<boolean>(false);
  const [backgroundError, setBackgroundError] = useState<string>("");
  const [backgroundSuccess, setBackgroundSuccess] = useState<boolean>(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState<boolean>(false);

  // Fetch options from backend on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const [credentialsRes, philosophyRes, expertiseRes] = await Promise.all([
          apiService.getCredentialsOptions(),
          apiService.getTrainingPhilosophyOptions(),
          apiService.getAreasOfExpertiseOptions(),
        ]);

        if ((credentialsRes as Record<string, unknown>).Code === 200) {
          setCredentialOptions(
            ((credentialsRes as Record<string, unknown>).options as string[]) || [],
          );
        }
        if ((philosophyRes as Record<string, unknown>).Code === 200) {
          setTrainingPhilosophyOptions(
            ((philosophyRes as Record<string, unknown>).options as string[]) || [],
          );
        }
        if ((expertiseRes as Record<string, unknown>).Code === 200) {
          setAreasOfExpertiseOptions(
            ((expertiseRes as Record<string, unknown>).options as string[]) || [],
          );
        }
      } catch (err) {
        console.error("Error fetching options:", err);
        setError("Failed to load options. Please refresh the page.");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // Update email when userData changes
  useEffect(() => {
    if (userData?.email) {
      setFormData((prev) => ({
        ...prev,
        email: userData.email ?? "",
      }));
    }
  }, [userData]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  useEffect(() => {
    const snapshot: TrainerSetupDraft = {
      formData,
      activeTab,
      completedTabs: Array.from(completedTabs),
      uploadedDocuments,
      backgroundForm: {
        legal_name: backgroundForm.legal_name,
        date_of_birth: backgroundForm.date_of_birth,
      },
    };
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (err) {
      console.error("Failed to persist trainer setup draft:", err);
    }
  }, [
    formData,
    activeTab,
    completedTabs,
    uploadedDocuments,
    backgroundForm.legal_name,
    backgroundForm.date_of_birth,
  ]);

  useEffect(() => {
    if (activeTab === "background") {
      setBackgroundForm((prev) => ({
        ...prev,
        legal_name:
          prev.legal_name || `${formData.first_name || ""} ${formData.last_name || ""}`.trim(),
      }));
    }
  }, [activeTab, formData.first_name, formData.last_name]);

  // Handle payment success callback from Stripe Payment Link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trainerId = urlParams.get("trainer_id");
    const paymentSuccess = urlParams.get("payment") === "success";

    if (trainerId && paymentSuccess) {
      const verifyPayment = async () => {
        try {
          const response = await apiService.verifyTrainerRegistrationPayment({
            trainer_id: trainerId,
          });

          if (response.Code === 200 && response.payment_status === "completed") {
            // Payment verified — show the confirmation screen and let the trainer
            // choose when to continue into the portal.
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            localStorage.setItem("trainerProfileCompleted", "true");
            // Strip the Stripe query params so a refresh doesn't re-run verification.
            window.history.replaceState({}, "", window.location.pathname);
            setPaymentConfirmed(true);
          } else {
            setError("Payment verification failed. Please contact support.");
          }
        } catch (err) {
          console.error("Payment verification error:", err);
          setError("Failed to verify payment. Please contact support.");
        }
      };

      verifyPayment();
    }
  }, [onSetupComplete]);

  // Time zone options
  const timeZoneOptions: string[] = [
    "Pacific Time",
    "Mountain Time",
    "Central Time",
    "Eastern Time",
    "Alaska Time (AKT)",
    "Hawaii Time (HST)",
  ];

  // US States options
  const stateOptions: StateOption[] = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayToggle = (field: string, value: string): void => {
    setFormData((prev) => {
      const currentArray = prev[field] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(value)
          ? currentArray.filter((item: string) => item !== value)
          : [...currentArray, value],
      };
    });
  };

  const handleTabChange = (tab: string): void => {
    setActiveTab(tab);
  };

  const validateBasicInfo = (): boolean => {
    return !!(
      formData.first_name &&
      formData.last_name &&
      formData.phone_number &&
      formData.business_street &&
      formData.business_city &&
      formData.business_state &&
      formData.business_zip &&
      formData.time_zone
    );
  };

  const validateCredentialsInfo = (): boolean => {
    return formData.credentials.length > 0;
  };

  const validatePhilosophyInfo = (): boolean => {
    return formData.training_philosophy.length > 0 && formData.areas_of_expertise.length > 0;
  };

  const validateRatesInfo = (): boolean => {
    const first10MinRate = parseFloat(formData.first_10_min_rate);
    const perMinRate = parseFloat(formData.per_minute_rate);
    return !!(
      formData.experience_years &&
      formData.first_10_min_rate &&
      formData.per_minute_rate &&
      formData.bio &&
      !isNaN(first10MinRate) &&
      first10MinRate >= 10.0 &&
      !isNaN(perMinRate) &&
      perMinRate >= 1.0
    );
  };

  const validateLegalInfo = (): boolean => {
    // Legal states are now optional - no requirement
    return true;
  };

  const validateDocumentsInfo = (): boolean => {
    // Insurance certificate is required; other documents are optional.
    return !!(uploadedDocuments.insurance || pendingFiles.insurance);
  };

  const handleNext = (): void => {
    const currentTab = activeTab;
    let nextTab = "";

    switch (currentTab) {
      case "intro":
        nextTab = "basic";
        setCompletedTabs((prev) => new Set([...prev, "intro"]));
        break;
      case "basic":
        if (validateBasicInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "basic"]));
          nextTab = "credentials";
        }
        break;
      case "credentials":
        if (validateCredentialsInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "credentials"]));
          nextTab = "philosophy";
        }
        break;
      case "philosophy":
        if (validatePhilosophyInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "philosophy"]));
          nextTab = "rates";
        }
        break;
      case "rates":
        if (validateRatesInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "rates"]));
          nextTab = "legal";
        }
        break;
      case "legal":
        if (validateLegalInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "legal"]));
          nextTab = "documents";
        }
        break;
      case "documents":
        if (validateDocumentsInfo()) {
          setCompletedTabs((prev) => new Set([...prev, "documents"]));
          nextTab = "background";
        }
        break;
      case "background":
        if (backgroundForm.consent && backgroundForm.legal_name) {
          setCompletedTabs((prev) => new Set([...prev, "background"]));
        }
        break;
    }

    if (nextTab) {
      setActiveTab(nextTab);
    }
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    console.log("=== SUBMITTING TRAINER SETUP ===");
    console.log("Form data:", formData);
    console.log("validateBasicInfo():", validateBasicInfo());
    console.log("validateCredentialsInfo():", validateCredentialsInfo());
    console.log("validatePhilosophyInfo():", validatePhilosophyInfo());
    console.log("validateRatesInfo():", validateRatesInfo());
    console.log("validateLegalInfo():", validateLegalInfo());

    // Check which fields are missing
    if (!validateBasicInfo()) {
      setError("Please complete all Basic Info fields");
      return;
    }
    if (!validateCredentialsInfo()) {
      setError("Please select at least one Credential");
      return;
    }
    if (!validatePhilosophyInfo()) {
      setError("Please select at least one Training Philosophy and Area of Expertise");
      return;
    }
    if (!validateRatesInfo()) {
      setError("Please complete all Experience & Rates fields");
      return;
    }
    if (!validateDocumentsInfo()) {
      setError("Please upload your Insurance Certificate to continue");
      return;
    }
    // Legal states validation removed - now optional

    try {
      setLoading(true);
      setError(null);

      // Build the full business address
      let fullAddress = formData.business_street;
      if (formData.business_apt) {
        fullAddress += ", " + formData.business_apt;
      }
      fullAddress +=
        ", " +
        formData.business_city +
        ", " +
        formData.business_state +
        " " +
        formData.business_zip;

      // Create trainer profile
      const response = await apiService.createTrainer({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        address: fullAddress, // Use business address for the address field
        business_name: formData.business_name,
        time_zone: formData.time_zone,
        business_address: fullAddress,
        credentials: formData.credentials,
        training_philosophy: formData.training_philosophy,
        areas_of_expertise: formData.areas_of_expertise,
        experience_years: parseInt(formData.experience_years),
        year_began_training: formData.year_began_training
          ? parseInt(formData.year_began_training)
          : null,
        first_10_min_rate: parseFloat(parseFloat(formData.first_10_min_rate).toFixed(2)),
        per_minute_rate: parseFloat(parseFloat(formData.per_minute_rate).toFixed(2)),
        bio: formData.bio,
        legal_states: formData.legal_states,
      });

      if (response.Code === 200) {
        // Store trainer ID for future use
        localStorage.setItem("trainerId", response.id as string);

        // Upload any pending documents
        const uploadPromises = Object.entries(pendingFiles).map(([docType, file]) =>
          handleFileUpload(docType, file).catch((err) => {
            console.error(`Failed to upload ${docType}:`, err);
            return null;
          }),
        );

        // Wait for all uploads to complete (or fail) before redirecting
        await Promise.all(uploadPromises);

        setCompletedTabs((prev) => new Set([...prev, "documents"]));
        setBackgroundError("");
        setBackgroundSuccess(false);
        setBackgroundForm((prev) => ({
          ...prev,
          legal_name: `${formData.first_name || ""} ${formData.last_name || ""}`.trim(),
        }));
        setActiveTab("background");
      } else {
        setError((response.Message as string) || "Failed to create trainer profile");
      }
    } catch (err) {
      console.error("Failed to create trainer profile:", err);
      setError((err as Error).message || "Failed to create trainer profile");
    } finally {
      setLoading(false);
    }
  };

  const renderMultiSelect = (
    field: string,
    options: string[],
    title: string,
    required: boolean = false,
  ): React.ReactNode => {
    const fieldValues = formData[field] as string[];
    return (
      <div className="flex flex-col gap-2">
        <label className="text-[0.9375rem] font-semibold text-foreground tracking-tight">
          {title} {required && <span className="text-destructive">*</span>}
        </label>
        <div className="flex flex-col gap-4">
          {loadingOptions ? (
            <div className="text-center text-primary text-lg mt-5">Loading options...</div>
          ) : options.length === 0 ? (
            <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mt-5 text-center">
              No options available. Please refresh the page.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5 max-h-[300px] overflow-y-auto p-2.5 bg-muted rounded-lg border border-border">
                {options.map((option) => (
                  <div
                    key={option}
                    onClick={() => handleArrayToggle(field, option)}
                    className={`px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-150 flex items-center justify-between text-sm font-medium ${
                      fieldValues.includes(option)
                        ? "bg-primary border border-primary text-primary-foreground font-semibold"
                        : "bg-card border border-border text-foreground"
                    }`}
                  >
                    {option}
                    {fieldValues.includes(option) && (
                      <span className="absolute right-4 text-base font-bold">✓</span>
                    )}
                  </div>
                ))}
              </div>
              {fieldValues.length > 0 && (
                <div className="px-4 py-2.5 bg-success/10 border border-success/30 rounded-lg text-success text-[0.9rem]">
                  <strong>Selected: </strong>
                  {fieldValues.join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderIntroTab = (): React.ReactNode => (
    <div className="flex flex-col gap-6">
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-7 flex flex-col gap-5 shadow-lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground tracking-tight">
          Welcome to the Trainer Network Application
        </h2>
        <p className="text-muted-foreground text-base leading-relaxed">
          We're thrilled you're here! This guided setup walks you through the information we need to
          showcase your expertise to potential clients.
        </p>
        <ul className="m-0 p-0 list-none flex flex-col gap-4">
          <li className="flex gap-3.5 text-foreground leading-relaxed text-base">
            <span className="text-2xl">📋</span>
            <span>
              <strong>Gather key details.</strong> We'll cover your business basics, professional
              experience, training philosophy, and rates.
            </span>
          </li>
          <li className="flex gap-3.5 text-foreground leading-relaxed text-base">
            <span className="text-2xl">🧾</span>
            <span>
              <strong>Document Upload.</strong> Add resumes, certifications, or degrees to
              strengthen your profile.
            </span>
          </li>
          <li className="flex gap-3.5 text-foreground leading-relaxed text-base">
            <span className="text-2xl">🛡️</span>
            <span>
              <strong>Background check authorization.</strong> We partner with a secure provider and
              store only your approval status.
            </span>
          </li>
        </ul>
        <div className="text-muted-foreground text-base leading-relaxed">
          You will be required to upload proof of liability insurance covering your use of this
          platform. You MAY also upload a resume, CV, certifications, degrees, etc to show your
          education and skills.
        </div>
        <div className="text-muted-foreground text-base leading-relaxed">
          You can revisit any section before submitting. Once everything looks good, we'll guide you
          through the background check to complete onboarding.
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={handleNext} className="font-semibold rounded-lg" size="md">
            Get Started →
          </Button>
        </div>
      </div>
    </div>
  );

  const renderBasicInfoTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Basic Profile Information
      </h2>
      <form className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                First Name <span className="text-destructive">*</span>
              </span>
            }
            value={formData.first_name}
            onChange={(e) =>
              handleInputChange({ target: { name: "first_name", value: e.target.value } })
            }
            placeholder="Enter your first name"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Last Name <span className="text-destructive">*</span>
              </span>
            }
            value={formData.last_name}
            onChange={(e) =>
              handleInputChange({ target: { name: "last_name", value: e.target.value } })
            }
            placeholder="Enter your last name"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        </div>

        <TextInput
          label={
            <span className="text-[0.9375rem] font-semibold text-foreground">
              Business Name (if applicable)
            </span>
          }
          value={formData.business_name}
          onChange={(e) =>
            handleInputChange({ target: { name: "business_name", value: e.target.value } })
          }
          placeholder="e.g., Pawsitive Training Solutions"
          classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <TextInput
              label={
                <span className="text-[0.9375rem] font-semibold text-foreground">
                  Email Address <span className="text-destructive">*</span>
                </span>
              }
              value={formData.email}
              readOnly
              disabled
              classNames={{
                input: "bg-muted border-border text-muted-foreground rounded-lg cursor-not-allowed",
              }}
            />
            <div className="text-[0.9rem] text-muted-foreground italic">
              Email from your account registration
            </div>
          </div>
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Phone Number <span className="text-destructive">*</span>
              </span>
            }
            value={formData.phone_number}
            onChange={(e) =>
              handleInputChange({
                target: { name: "phone_number", value: formatPhoneNumber(e.target.value) },
              })
            }
            placeholder="(xxx) xxx - xxxx"
            maxLength={16}
            inputMode="tel"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Select
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground tracking-tight">
                Time Zone
              </span>
            }
            value={formData.time_zone || null}
            onChange={(value) =>
              handleInputChange({ target: { name: "time_zone", value: value || "" } })
            }
            placeholder="Select your time zone..."
            data={timeZoneOptions}
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[0.9375rem] font-semibold text-foreground tracking-tight">
            Business Address / Mailing Address{" "}
          </label>
        </div>
        <TextInput
          label={
            <span className="text-sm font-medium text-muted-foreground">
              Street Address <span className="text-destructive">*</span>
            </span>
          }
          value={formData.business_street}
          onChange={(e) =>
            handleInputChange({ target: { name: "business_street", value: e.target.value } })
          }
          placeholder="123 Main Street"
          classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
        />
        <TextInput
          label={<span className="text-sm font-medium text-muted-foreground">Apt, Ste, etc.</span>}
          value={formData.business_apt}
          onChange={(e) =>
            handleInputChange({ target: { name: "business_apt", value: e.target.value } })
          }
          placeholder="Apt 4B"
          classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
        />
        <div className="flex flex-col sm:flex-row gap-2.5 mb-5">
          <div className="flex flex-col gap-2 flex-[2]">
            <TextInput
              label={
                <span className="text-sm font-medium text-muted-foreground">
                  City <span className="text-destructive">*</span>
                </span>
              }
              value={formData.business_city}
              onChange={(e) =>
                handleInputChange({ target: { name: "business_city", value: e.target.value } })
              }
              placeholder="City"
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
          </div>
          <div className="flex flex-col gap-2 flex-1 mr-2.5">
            <Select
              label={
                <span className="text-sm font-medium text-muted-foreground">
                  State <span className="text-destructive">*</span>
                </span>
              }
              value={formData.business_state}
              onChange={(value) =>
                handleInputChange({ target: { name: "business_state", value: value || "" } })
              }
              placeholder="Select state"
              searchable
              data={stateOptions}
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <TextInput
              label={
                <span className="text-sm font-medium text-muted-foreground">
                  ZIP Code <span className="text-destructive">*</span>
                </span>
              }
              value={formData.business_zip}
              onChange={(e) =>
                handleInputChange({ target: { name: "business_zip", value: e.target.value } })
              }
              placeholder="ZIP"
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
          </div>
        </div>

        <Button
          onClick={handleNext}
          className={`self-end mt-[30px] font-semibold rounded-lg ${!validateBasicInfo() ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={!validateBasicInfo()}
          size="md"
        >
          Next →
        </Button>
      </form>
    </div>
  );

  const renderCredentialsTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Credentials & Certifications
      </h2>
      <form className="flex flex-col gap-5">
        {renderMultiSelect("credentials", credentialOptions, "Credential Filter", true)}

        {formData.credentials.includes("Formal degree") && (
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Formal Degree - Area of Study
              </span>
            }
            value={formData.formal_degree}
            onChange={(e) =>
              handleInputChange({ target: { name: "formal_degree", value: e.target.value } })
            }
            placeholder="e.g., Animal Behavior, Veterinary Science, Psychology"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        )}

        {formData.credentials.includes("Professional Dog Trainer School") && (
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Professional Dog Trainer School
              </span>
            }
            value={formData.professional_school}
            onChange={(e) =>
              handleInputChange({ target: { name: "professional_school", value: e.target.value } })
            }
            placeholder="e.g., Karen Pryor Academy, CATCH Canine Trainers Academy"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        )}

        {formData.credentials.includes("CCFT – list affiliation") && (
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                CCFT Affiliation
              </span>
            }
            value={formData.ccf_affiliation}
            onChange={(e) =>
              handleInputChange({ target: { name: "ccf_affiliation", value: e.target.value } })
            }
            placeholder="List your CCFT affiliation"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        )}

        {formData.credentials.includes("Other sport judge") && (
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Other Sport Judge
              </span>
            }
            value={formData.other_sport_judge}
            onChange={(e) =>
              handleInputChange({ target: { name: "other_sport_judge", value: e.target.value } })
            }
            placeholder="List other sports you judge"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        )}

        {formData.credentials.includes("Breed expert") && (
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">Breed Expert</span>
            }
            value={formData.breed_expert}
            onChange={(e) =>
              handleInputChange({ target: { name: "breed_expert", value: e.target.value } })
            }
            placeholder="List breeds you specialize in"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        )}

        <Textarea
          label={
            <span className="text-[0.9375rem] font-semibold text-foreground">
              Other Credentials
            </span>
          }
          value={formData.other_credentials}
          onChange={(e) =>
            handleInputChange({ target: { name: "other_credentials", value: e.target.value } })
          }
          placeholder="List any other relevant credentials or certifications..."
          rows={3}
          classNames={{
            input: "bg-input border-border text-foreground rounded-lg min-h-[120px] font-[inherit]",
          }}
        />

        <Button
          onClick={handleNext}
          className={`self-end mt-[30px] font-semibold rounded-lg ${!validateCredentialsInfo() ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={!validateCredentialsInfo()}
          size="md"
        >
          Next →
        </Button>
      </form>
    </div>
  );

  const renderPhilosophyTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Training Philosophy & Methods
      </h2>
      <form className="flex flex-col gap-5">
        {renderMultiSelect(
          "training_philosophy",
          trainingPhilosophyOptions,
          "Training Philosophy and Methods",
          true,
        )}
        {renderMultiSelect(
          "areas_of_expertise",
          areasOfExpertiseOptions,
          "Areas of Expertise and Specializations",
          true,
        )}

        <Button
          onClick={handleNext}
          className={`self-end mt-[30px] font-semibold rounded-lg ${!validatePhilosophyInfo() ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={!validatePhilosophyInfo()}
          size="md"
        >
          Next →
        </Button>
      </form>
    </div>
  );

  const renderRatesTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Experience & Rates
      </h2>
      <form className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Years of Experience <span className="text-destructive">*</span>
              </span>
            }
            type="number"
            value={formData.experience_years}
            onChange={(e) =>
              handleInputChange({ target: { name: "experience_years", value: e.target.value } })
            }
            min={0}
            max={50}
            placeholder="5"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
          <TextInput
            label={
              <span className="text-[0.9375rem] font-semibold text-foreground">
                Year Began Training Professionally
              </span>
            }
            type="number"
            value={formData.year_began_training}
            onChange={(e) =>
              handleInputChange({ target: { name: "year_began_training", value: e.target.value } })
            }
            min={1950}
            max={new Date().getFullYear()}
            placeholder="2019"
            classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          />
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-xl p-5 mb-5">
          <div className="text-xl font-semibold text-primary mb-2.5">Session Pricing Structure</div>
          <div className="text-muted-foreground leading-relaxed">
            Set your rates for the first 10 minutes and per-minute rate after that.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <TextInput
              label={
                <span className="text-[0.9375rem] font-semibold text-foreground">
                  First 10 Minutes Rate ($){" "}
                  <span className="text-[0.85rem] text-muted-foreground ml-2">
                    (Minimum: $10.00)
                  </span>
                </span>
              }
              value={formData.first_10_min_rate}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, numbers, and one decimal point
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  handleInputChange({ target: { name: "first_10_min_rate", value } });
                }
              }}
              onBlur={(e) => {
                // Validate and format on blur
                const value = e.target.value.trim();
                if (value === "") {
                  handleInputChange({ target: { name: "first_10_min_rate", value: "" } });
                  return;
                }
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  // Enforce minimum of 10.00
                  const validatedValue = Math.max(10.0, numValue);
                  const formatted = parseFloat(validatedValue.toFixed(2));
                  handleInputChange({
                    target: { name: "first_10_min_rate", value: formatted.toString() },
                  });
                }
              }}
              placeholder="15.00"
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
            {formData.first_10_min_rate && parseFloat(formData.first_10_min_rate) < 10 && (
              <div className="text-destructive text-[0.85rem] mt-1">Minimum rate is $10.00</div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <TextInput
              label={
                <span className="text-[0.9375rem] font-semibold text-foreground">
                  Per Minute Rate After 10 Min ($){" "}
                  <span className="text-[0.85rem] text-muted-foreground ml-2">
                    (Minimum: $1.00)
                  </span>
                </span>
              }
              value={formData.per_minute_rate}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string, numbers, and one decimal point
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  handleInputChange({ target: { name: "per_minute_rate", value } });
                }
              }}
              onBlur={(e) => {
                // Validate and format on blur
                const value = e.target.value.trim();
                if (value === "") {
                  handleInputChange({ target: { name: "per_minute_rate", value: "" } });
                  return;
                }
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  // Enforce minimum of 1.00
                  const validatedValue = Math.max(1.0, numValue);
                  const formatted = parseFloat(validatedValue.toFixed(2));
                  handleInputChange({
                    target: { name: "per_minute_rate", value: formatted.toString() },
                  });
                }
              }}
              placeholder="1.00"
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
            {formData.per_minute_rate && parseFloat(formData.per_minute_rate) < 1 && (
              <div className="text-destructive text-[0.85rem] mt-1">
                Minimum rate is $1.00 per minute
              </div>
            )}
          </div>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-xl p-5 mb-5">
          <div className="text-xl font-semibold text-primary mb-2.5">💡 Pricing Preview</div>
          <div className="text-muted-foreground leading-relaxed">
            Your sessions will be displayed as:{" "}
            <strong>
              ${formData.first_10_min_rate || "15.00"} first 10 minutes + $
              {formData.per_minute_rate || "1.17"}/min after
            </strong>
            <br />
            This pricing structure encourages quick, focused sessions while providing fair
            compensation for longer training sessions.
          </div>
        </div>

        <Textarea
          label={
            <span className="text-[0.9375rem] font-semibold text-foreground">
              Bio <span className="text-destructive">*</span>
            </span>
          }
          value={formData.bio}
          onChange={(e) => handleInputChange({ target: { name: "bio", value: e.target.value } })}
          placeholder="Tell clients about your training approach, certifications, experience, and what makes you special as a dog trainer..."
          classNames={{
            input: "bg-input border-border text-foreground rounded-lg min-h-[120px] font-[inherit]",
          }}
        />

        <Button
          onClick={handleNext}
          className={`self-end mt-[30px] font-semibold rounded-lg ${!validateRatesInfo() ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={!validateRatesInfo()}
          size="md"
        >
          Next →
        </Button>
      </form>
    </div>
  );

  const handleSelectAllStates = (): void => {
    if (formData.legal_states.length === stateOptions.length) {
      // If all are selected, deselect all
      setFormData((prev) => ({
        ...prev,
        legal_states: [],
      }));
    } else {
      // Select all states
      setFormData((prev) => ({
        ...prev,
        legal_states: stateOptions.map((s) => s.value),
      }));
    }
  };

  const renderLegalTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Legal Permissions (Optional)
      </h2>
      <form className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label className="text-[0.9375rem] font-semibold text-foreground tracking-tight">
            I am legally permitted to train dogs in the following states (optional):
          </label>
          <div className="mb-4">
            <Button
              type="button"
              onClick={handleSelectAllStates}
              className="mb-4 font-semibold rounded-lg"
              color={formData.legal_states.length === stateOptions.length ? "red" : undefined}
              size="sm"
            >
              {formData.legal_states.length === stateOptions.length ? "Uncheck All" : "Check All"}
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5 max-h-[300px] overflow-y-auto p-2.5 bg-muted rounded-lg border border-border">
              {stateOptions.map((state) => (
                <div
                  key={state.value}
                  onClick={() => handleArrayToggle("legal_states", state.value)}
                  className={`px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-150 flex items-center justify-between text-sm font-medium ${
                    formData.legal_states.includes(state.value)
                      ? "bg-primary border border-primary text-primary-foreground font-semibold"
                      : "bg-card border border-border text-foreground"
                  }`}
                >
                  {state.label}
                  {formData.legal_states.includes(state.value) && (
                    <span className="absolute right-4 text-base font-bold">✓</span>
                  )}
                </div>
              ))}
            </div>
            {formData.legal_states.length > 0 && (
              <div className="px-4 py-2.5 bg-success/10 border border-success/30 rounded-lg text-success text-[0.9rem]">
                <strong>Selected States: </strong>
                {formData.legal_states
                  .map((state) => stateOptions.find((s) => s.value === state)?.label)
                  .join(", ")}
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={handleNext}
          className="self-end mt-[30px] font-semibold rounded-lg"
          size="md"
        >
          Next →
        </Button>
      </form>
    </div>
  );

  const handleFileSelect = (documentType: string, file: File): void => {
    if (!file) return;
    // Store file for upload after profile creation
    setPendingFiles((prev) => ({
      ...prev,
      [documentType]: file,
    }));
    setUploadedDocuments((prev) => ({
      ...prev,
      [documentType]: { filename: file.name, pending: true },
    }));
  };

  const handleFileUpload = async (documentType: string, file: File): Promise<void> => {
    if (!file) return;

    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) {
      setError("Please complete trainer setup first");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.uploadTrainerDocument(trainerId, documentType, file);
      if (response.Code === 200) {
        setUploadedDocuments((prev) => ({
          ...prev,
          [documentType]: {
            filename: response.filename as string,
            file_path: response.file_path as string,
          },
        }));
        setPendingFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[documentType];
          return newFiles;
        });
        setError(null);
      } else {
        setError((response.Message as string) || "Failed to upload document");
      }
    } catch (err) {
      console.error("Error uploading document:", err);
      setError("Failed to upload document");
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = async (documentType: string): Promise<void> => {
    const ok = await confirm({
      title: "Delete Document",
      message: `Are you sure you want to delete this ${documentType}?`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;

    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) {
      setError("Trainer ID not found");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteTrainerDocument(trainerId, documentType);
      if (response.Code === 200) {
        setUploadedDocuments((prev) => ({
          ...prev,
          [documentType]: null,
        }));
        setError(null);
      } else {
        setError((response.Message as string) || "Failed to delete document");
      }
    } catch (err) {
      console.error("Error deleting document:", err);
      setError("Failed to delete document");
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentField = (
    documentType: string,
    label: string,
    accept: string,
    multiple: boolean = false,
    required: boolean = false,
  ): React.ReactNode => {
    const uploadedFile = uploadedDocuments[documentType];

    return (
      <div className="flex flex-col gap-2">
        <label className="text-[0.9375rem] font-semibold text-foreground tracking-tight">
          {label} {required && <span className="text-destructive">*</span>}
        </label>
        {uploadedFile ? (
          <div className="flex items-center gap-2.5 mt-2">
            <span className={uploadedFile.pending ? "text-warning" : "text-success"}>
              {uploadedFile.pending ? "⏳" : "✓"} {uploadedFile.filename}
              {uploadedFile.pending && " (will upload after profile creation)"}
            </span>
            <Button
              type="button"
              onClick={() => {
                if (uploadedFile.pending) {
                  setUploadedDocuments((prev) => ({
                    ...prev,
                    [documentType]: null,
                  }));
                  setPendingFiles((prev) => {
                    const newFiles = { ...prev };
                    delete newFiles[documentType];
                    return newFiles;
                  });
                } else {
                  handleFileDelete(documentType);
                }
              }}
              color="red"
              radius="md"
              size="xs"
            >
              {uploadedFile.pending ? "Remove" : "Delete"}
            </Button>
          </div>
        ) : (
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            className="p-4 rounded-xl border-2 border-border bg-input text-foreground text-base cursor-pointer"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileSelect(documentType, file);
              }
            }}
          />
        )}
      </div>
    );
  };

  const renderDocumentsTab = (): React.ReactNode => (
    <div>
      <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
        Document Upload
      </h2>
      <form className="flex flex-col gap-5">
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-5 mb-5">
          <div className="text-xl font-semibold text-primary mb-2.5">Document Upload</div>
          <div className="text-muted-foreground leading-relaxed">
            Upload your resume, certificates, degrees, and insurance certificate. These documents
            will be stored securely and can be updated later. An insurance certificate is required
            to continue; all other uploads are optional. You can delete and re-upload files if
            needed.
          </div>
        </div>

        {renderDocumentField("resume", "Resume", ".pdf,.doc,.docx")}
        {renderDocumentField(
          "certificates",
          "Certificates",
          ".pdf,.doc,.docx,.jpg,.jpeg,.png",
          true,
        )}
        {renderDocumentField("degrees", "Degrees", ".pdf,.doc,.docx,.jpg,.jpeg,.png", true)}
        {renderDocumentField(
          "insurance",
          "Insurance Certificate",
          ".pdf,.doc,.docx,.jpg,.jpeg,.png",
          false,
          true,
        )}

        <Button
          onClick={handleSubmit}
          className={`self-end mt-5 font-semibold rounded-lg ${loading || !validateDocumentsInfo() ? "opacity-60 cursor-not-allowed" : ""}`}
          disabled={loading || !validateDocumentsInfo()}
          size="md"
        >
          {loading ? "Saving Profile..." : "Continue to Background Check"}
        </Button>
      </form>
    </div>
  );

  const handleBackgroundSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBackgroundError("");
    setBackgroundSuccess(false);

    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) {
      setBackgroundError(
        "We could not locate your trainer profile. Please save your profile information first.",
      );
      return;
    }

    if (!backgroundForm.legal_name.trim()) {
      setBackgroundError("Please provide your full legal name.");
      return;
    }

    if (!backgroundForm.date_of_birth) {
      setBackgroundError("Please provide your date of birth.");
      return;
    }

    if (!backgroundForm.consent) {
      setBackgroundError("You must authorize the background check to continue.");
      return;
    }

    try {
      setBackgroundSubmitting(true);
      await apiService.submitBackgroundCheck({
        trainer_id: trainerId,
        legal_name: backgroundForm.legal_name.trim(),
        date_of_birth: backgroundForm.date_of_birth,
        consent: true,
      });

      setBackgroundSuccess(true);
      setCompletedTabs((prev) => new Set([...prev, "background"]));

      // Redirect to payment
      try {
        const checkoutResponse = await apiService.createTrainerRegistrationCheckout({
          trainer_id: trainerId,
        });

        if (checkoutResponse.Code === 200) {
          if (checkoutResponse.url) {
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            // Redirect to Stripe Payment Link
            window.location.href = checkoutResponse.url as string;
          } else {
            setBackgroundError("Failed to get payment link. Please try again.");
            setBackgroundSubmitting(false);
          }
        } else {
          setBackgroundError(
            (checkoutResponse.Message as string) || "Failed to initiate payment. Please try again.",
          );
          setBackgroundSubmitting(false);
        }
      } catch (paymentErr) {
        console.error("Payment initiation failed:", paymentErr);
        setBackgroundError("Failed to initiate payment. Please try again.");
        setBackgroundSubmitting(false);
      }
    } catch (err) {
      console.error("Background check submission failed:", err);
      setBackgroundError(
        (err as Error).message || "Failed to submit background check. Please try again.",
      );
      setBackgroundSubmitting(false);
    }
  };

  const renderBackgroundCheckTab = (): React.ReactNode => {
    const trainerId = localStorage.getItem("trainerId");

    return (
      <div>
        <h2 className="text-xl sm:text-2xl md:text-[1.75rem] font-semibold mb-6 text-foreground tracking-tight">
          Background Check & Final Review
        </h2>
        <div className="bg-info/10 border border-info/25 rounded-xl p-6 mb-6 text-foreground">
          <div className="text-[1.3rem] font-semibold text-info mb-2.5">Why this matters</div>
          <p className="text-base leading-relaxed text-muted-foreground mb-2.5">
            Completing your background check is the final step toward joining the Pheefee trainer
            network. We use a third-party provider and do <strong>not</strong> store any of your
            sensitive background check data. Only the approval status is retained in your profile.
          </p>
          <ul className="pl-5 text-muted-foreground leading-relaxed">
            <li>Review the information below carefully before submitting.</li>
            <li>
              Your submission authorizes Pheefee to run a background check for platform eligibility.
            </li>
            <li>We will email you once your screening is complete.</li>
          </ul>
        </div>

        {!trainerId && (
          <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mt-5 text-center">
            We couldn't locate your trainer profile. Please save your profile information first.
          </div>
        )}

        <form className="flex flex-col gap-5" onSubmit={handleBackgroundSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <TextInput
              label={
                <span className="text-[0.9375rem] font-semibold text-foreground">
                  Legal Name <span className="text-destructive">*</span>
                </span>
              }
              value={backgroundForm.legal_name}
              onChange={(e) =>
                setBackgroundForm((prev) => ({ ...prev, legal_name: e.target.value }))
              }
              placeholder="Enter your full legal name"
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
            <TextInput
              label={
                <span className="text-[0.9375rem] font-semibold text-foreground">
                  Date of Birth <span className="text-destructive">*</span>
                </span>
              }
              type="date"
              value={backgroundForm.date_of_birth}
              onChange={(e) =>
                setBackgroundForm((prev) => ({ ...prev, date_of_birth: e.target.value }))
              }
              classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
            />
          </div>

          <div className="bg-success/10 border border-success/30 rounded-xl p-[18px] mt-5 text-foreground">
            <label className="flex items-start gap-3 text-base leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={backgroundForm.consent}
                onChange={(e) =>
                  setBackgroundForm((prev) => ({ ...prev, consent: e.target.checked }))
                }
                className="w-[18px] h-[18px] mt-1"
              />
              <span>
                I authorize Pheefee to run a background check using the information above. I
                understand that Pheefee will not store my background check details—only the approval
                status needed for my trainer profile.
              </span>
            </label>
          </div>

          {backgroundError && (
            <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mt-5 text-center">
              {backgroundError}
            </div>
          )}
          {backgroundSuccess && (
            <div className="bg-success/10 border border-success/30 text-success p-4 rounded-xl mt-5 text-center">
              Background check submitted! We'll notify you once review is complete.
            </div>
          )}

          <Button
            type="submit"
            className={`self-end mt-5 font-semibold rounded-lg ${backgroundSubmitting ? "opacity-60 cursor-not-allowed" : ""}`}
            disabled={backgroundSubmitting || !trainerId}
            size="md"
          >
            {backgroundSubmitting ? "Processing..." : "Pay and Submit"}
          </Button>
        </form>
      </div>
    );
  };

  const renderCurrentTab = (): React.ReactNode => {
    switch (activeTab) {
      case "intro":
        return renderIntroTab();
      case "basic":
        return renderBasicInfoTab();
      case "credentials":
        return renderCredentialsTab();
      case "philosophy":
        return renderPhilosophyTab();
      case "rates":
        return renderRatesTab();
      case "legal":
        return renderLegalTab();
      case "documents":
        return renderDocumentsTab();
      case "background":
        return renderBackgroundCheckTab();
      default:
        return renderBasicInfoTab();
    }
  };

  const tabs: TabInfo[] = [
    { id: "intro", label: "Welcome", icon: "👋", short: "Intro" },
    { id: "basic", label: "Basic Info", icon: "", short: "Basic" },
    { id: "credentials", label: "Credentials", icon: "", short: "Credentials" },
    { id: "philosophy", label: "Philosophy", icon: "", short: "Philosophy" },
    { id: "rates", label: "Rates & Bio", icon: "", short: "Rates" },
    { id: "legal", label: "Legal States", icon: "", short: "Legal" },
    { id: "documents", label: "Documents", icon: "", short: "Docs" },
    { id: "background", label: "Background Check", icon: "", short: "Background" },
  ];

  if (paymentConfirmed) {
    return (
      <div className="font-sans bg-background min-h-screen text-foreground flex flex-col">
        <header className="bg-card text-foreground py-5 shadow-sm border-b border-border">
          <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <img src="/images/logo.png" alt="Pheefee Logo" className="w-10 h-10 object-contain" />
              <span className="text-2xl font-bold text-foreground">Pheefee</span>
            </div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-5">
          <div className="bg-card rounded-2xl border border-border shadow-lg max-w-[640px] w-full p-8 sm:p-10 text-center flex flex-col gap-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Thank you for submitting!
            </h1>
            <p className="text-base leading-relaxed text-muted-foreground">
              Background checks are usually processed in <strong>2–5 business days</strong>. During
              this time, we may also verify your listed credentials and provided insurance
              information. Please watch your email for updates.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              You may log in and edit your portal at any time.
            </p>
            <div className="flex justify-center">
              <Button onClick={onSetupComplete} size="md" className="font-semibold rounded-lg">
                Continue to Your Portal →
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-background min-h-screen text-foreground flex flex-col">
      {confirmModal}
      {/* Header */}
      <header className="bg-card text-foreground py-5 shadow-sm border-b border-border">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 cursor-pointer" onClick={onBackToHome}>
            <img src="/images/logo.png" alt="Pheefee Logo" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-bold text-foreground">Pheefee</span>
          </div>
          <Button onClick={onBackToHome} variant="default" radius="md">
            ← Back to Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col max-w-[1200px] mx-auto w-full p-3 sm:p-5">
        <div className="bg-info/10 border border-info/25 rounded-xl px-6 py-5 mb-8 text-foreground shadow-sm">
          <div className="text-[1.4rem] font-bold mb-2 text-info">Complete Every Step</div>
          <div className="text-base leading-relaxed text-muted-foreground">
            To be considered for the Pheefee trainer network you must finish <strong>all</strong>{" "}
            sections, including document review and the background check. We'll guide you through
            each requirement—stay the course!
          </div>
        </div>

        {/* Top Navigation Tabs */}
        <div className="flex items-center mb-8 overflow-x-auto pb-2 gap-3">
          {tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {index > 0 && (
                <div
                  className={`flex-1 h-[3px] rounded-sm relative ${
                    completedTabs.has(tabs[index - 1].id) ? "bg-success" : "bg-border"
                  }`}
                />
              )}
              <button
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-3.5 rounded-[10px] border-none text-[0.9375rem] font-medium cursor-pointer transition-all duration-200 flex items-center gap-2.5 whitespace-nowrap relative min-w-[140px] justify-center ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground font-semibold shadow-md -translate-y-0.5"
                    : completedTabs.has(tab.id)
                      ? "bg-card text-success border-2 !border-success font-semibold"
                      : "bg-card text-muted-foreground shadow-sm"
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span>{activeTab === tab.id ? tab.label : tab.short}</span>
                {completedTabs.has(tab.id) && <span className="ml-2 text-lg">✓</span>}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-card rounded-xl p-4 sm:p-8 border border-border overflow-y-auto shadow-lg min-h-[500px]">
          {renderCurrentTab()}

          {error && (
            <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl mt-5 text-center">
              {error}
            </div>
          )}
          {loading && (
            <div className="text-center text-primary text-lg mt-5">
              Setting up your trainer profile...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainerSetup;
